import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Layout from "./components/Layout";
import Pricing from "./pages/Pricing";
import Riders from "./pages/Riders";
import Drivers from "./pages/Drivers";
import RideDetails from "./pages/RideDetails";
import TestRide from "./pages/TestRide";
import NotFound from "./pages/NotFound";
import { Documents } from "./pages/Documents";
import Rewards from "./pages/Rewards";
import { Toaster } from "react-hot-toast";

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
      light: "#42a5f5",
      dark: "#1565c0",
    },
    secondary: {
      main: "#f50057",
      light: "#ff4081",
      dark: "#c51162",
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
  },
});

// Protected route component
const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({
  element,
}) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? element : <Navigate to="/login" />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute element={<Layout />} />}>
              <Route index element={<Dashboard />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="riders" element={<Riders />} />
              <Route path="rewards" element={<Rewards />} />
              <Route path="drivers" element={<Drivers />} />
              <Route path="documents" element={<Documents />} />
              <Route path="rides/:rideId" element={<RideDetails />} />
              <Route path="test-ride" element={<TestRide />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
      <Toaster/>
    </ThemeProvider>
  );
};

export default App;
